import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Mail } from 'lucide-react';
import { validateEmail } from '../utils/validation';
import EmailInput from './ui/EmailInput';
import Button from './ui/Button';

type EmailVerificationModalProps = {
  onConfirm: (email: string) => void;
  loading: boolean;
};

export default function EmailVerificationModal({ onConfirm, loading }: EmailVerificationModalProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) setError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }
    onConfirm(email);
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#b3905e]/10 flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5 text-[#b3905e]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Verify Your Email</h2>
            <p className="text-sm text-gray-500">We need your email address to complete your application.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <EmailInput
            value={email}
            onChange={handleChange}
            error={error}
          />

          <Button
            type="submit"
            fullWidth
            size="default"
            disabled={!email || loading}
            loading={loading}
            loadingText="Submitting..."
          >
            Continue with this email
          </Button>
        </form>
      </div>
    </div>,
    document.body
  );
}
